import type { Product, ProductAvailability } from '@/api/types';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseIsoLocal(iso: string): Date | undefined {
  const [y, m, d] = iso.split('-').map((p) => Number.parseInt(p, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return undefined;
  }
  return new Date(y, m - 1, d);
}

/** ISO yyyy-mm-dd of today in the user's local timezone. */
export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isBookable(a: ProductAvailability): boolean {
  return (a.status ?? '').toLowerCase() === 'bookable';
}

export function bookableDateSet(p: Product | undefined): Set<string> {
  const out = new Set<string>();
  if (!p) return out;

  for (const a of p.availabilities ?? []) {
    if (!isBookable(a) || !a.startDate) continue;

    const endDate = a.endDate && a.endDate >= a.startDate ? a.endDate : a.startDate;
    const current = parseIsoLocal(a.startDate);
    const end = parseIsoLocal(endDate);

    if (!current || !end) continue;

    while (current <= end) {
      out.add(toIso(current));
      current.setDate(current.getDate() + 1);
    }
  }

  return out;
}

/** Returns true if `iso` (yyyy-mm-dd) is on or after today. */
export function isFutureOrToday(iso: string | undefined | null): boolean {
  if (!iso) return false;
  return iso >= todayIso();
}

/** First bookable availability with a start date in the future (or today). */
export function nextBookableAvailability(p: Product | undefined): ProductAvailability | undefined {
  if (!p) return undefined;
  const list = p.availabilities ?? [];
  const today = todayIso();
  return list.find((a) => isBookable(a) && (a.startDate ?? '') >= today);
}

export function nextBookableDate(p: Product | undefined): string | undefined {
  return nextBookableAvailability(p)?.startDate;
}

/** Earliest start date acceptable for the date picker. */
export function minStartDate(p: Product | undefined): string {
  const today = todayIso();
  const min = p?.minDate ?? today;
  return min > today ? min : today;
}

/** Latest future-or-today bookable date, used as the calendar upper bound. */
export function maxStartDate(p: Product | undefined): string | undefined {
  const list = p?.availabilities ?? [];
  const today = todayIso();
  const futureBookableEnds = list
    .filter((a) => isBookable(a) && a.startDate)
    .map((a) =>
      a.endDate && a.endDate >= (a.startDate as string)
        ? a.endDate
        : (a.startDate as string),
    )
    .filter((date) => date >= today)
    .sort();

  if (futureBookableEnds.length > 0) {
    return futureBookableEnds[futureBookableEnds.length - 1];
  }
  return p?.maxDate ?? undefined;
}
